import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuDownload, LuFileText, LuImage, LuTrash2 } from 'react-icons/lu';

import {
   Chip,
   ChipDelete,
   ChipDeleted,
   ChipDeletedName,
   ChipMain,
   ChipName,
   ChipRow,
   ChipSize,
} from '../../../../styles/dunning/attachments';
import { getDunningFileDownload } from '../../../../services/finance/dunningApi';
import { financeErrorMessage } from '../../utils/apiError';
import { formatFileSize, formatTimestamp } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Anexos JÁ GRAVADOS de uma tratativa, dentro do item da timeline (handoff
// 0096). Dois estados:
//
//  · ativo    → chip clicável: pede a URL presigned NO CLIQUE e abre em nova
//               aba. A URL vale 15min e nunca é cacheada (nem no state).
//  · excluído → rastro: riscado, esmaecido, SEM download, com quem/quando/
//               motivo no tooltip. A linha fica no caso de propósito — é a
//               evidência da exclusão (o arquivo em si sumiu do S3).
//
// A lixeira só aparece com `finance:dunning:interact` (0100 — era `:update`) e
// caso OPEN: o backend
// responde 422 `dunning_not_open` em caso encerrado, e mostrar um botão que só
// falha no clique convida ao erro (mesma postura do form de tratativa).
// ---------------------------------------------------------------------------

function fileIconFor(mimeType) {
   return String(mimeType ?? '').startsWith('image/') ? LuImage : LuFileText;
}

function deletedTooltip(file) {
   const parts = [`Anexo excluído por ${file.deleted_by?.nome ?? 'usuário removido'}`];
   if (file.deleted_at) parts.push(formatTimestamp(file.deleted_at));
   if (file.deleted_reason) parts.push(`Motivo: ${file.deleted_reason}`);
   return parts.join(' · ');
}

export default function AttachmentChips({
   caseId,
   interactionId,
   files,
   canDelete,
   onAskDelete,
}) {
   // Id do anexo cuja URL está sendo pedida — trava só aquele chip.
   const [downloadingId, setDownloadingId] = useState(null);

   const handleDownload = async (file) => {
      setDownloadingId(file.id);
      try {
         const { url } = await getDunningFileDownload(
            caseId,
            interactionId,
            file.id
         );
         if (!url) {
            toast.error('O servidor não devolveu o link do anexo.');
            return;
         }
         window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Não foi possível abrir o anexo.')
         );
      } finally {
         setDownloadingId(null);
      }
   };

   return (
      <ChipRow>
         {files.map((file) => {
            const Icon = fileIconFor(file.mime_type);

            if (file.deleted) {
               return (
                  <ChipDeleted key={file.id} title={deletedTooltip(file)}>
                     <Icon size={12} />
                     <ChipDeletedName>{file.original_name}</ChipDeletedName>
                     <ChipSize>excluído</ChipSize>
                  </ChipDeleted>
               );
            }

            const isDownloading = downloadingId === file.id;

            return (
               <Chip key={file.id}>
                  <ChipMain
                     type="button"
                     disabled={isDownloading}
                     title={`Abrir ${file.original_name} (enviado por ${
                        file.uploaded_by?.nome ?? 'usuário removido'
                     })`}
                     onClick={() => handleDownload(file)}
                  >
                     <Icon size={12} />
                     <ChipName>{file.original_name}</ChipName>
                     <ChipSize>{formatFileSize(file.size_bytes)}</ChipSize>
                     <LuDownload size={12} />
                  </ChipMain>
                  {canDelete && (
                     <ChipDelete
                        type="button"
                        title="Excluir anexo (exige motivo)"
                        aria-label={`Excluir ${file.original_name}`}
                        onClick={() => onAskDelete(interactionId, file)}
                     >
                        <LuTrash2 size={12} />
                     </ChipDelete>
                  )}
               </Chip>
            );
         })}
      </ChipRow>
   );
}

AttachmentChips.propTypes = {
   caseId: PropTypes.number.isRequired,
   interactionId: PropTypes.number.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   files: PropTypes.array.isRequired,
   canDelete: PropTypes.bool.isRequired,
   onAskDelete: PropTypes.func.isRequired,
};
